import { RELEASE_APRIL_TAX_SERVICES, RELEASE_APRIL_TAX_SERVICES_PHASE_2 } from '#client/constants/experimentsFlags';

import { TaxClassificationGate } from '../components/gates/TaxClassificationGate';
import { TaxPartnerExclusivityGate } from '../components/gates/TaxPartnerExclusivityGate';
import { getBuyButtonConfig } from '../utils/commonEntitlements';
import {
  BUSINESS_ENTITY_TAX_CLASSIFICATION,
  BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS,
  TAX_CLASSIFICATION_TO_SERVICE_MAP,
  TAX_CLASSIFICATION_TITLE_MAP
} from '../utils/taxUtils';

const DEFAULT_BULLET_POINTS = [
  { title: 'productPage.taxFiling.buyBox.bulletPoint1' },
  { title: 'productPage.taxFiling.buyBox.bulletPoint2' },
  { title: 'productPage.taxFiling.buyBox.bulletPoint3' }
];

const BULLET_POINTS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    { title: 'productPage.taxFiling.buyBox.singleMemberLlc.bulletPoint1' },
    { title: 'productPage.taxFiling.buyBox.singleMemberLlc.bulletPoint2' },
    { title: 'productPage.taxFiling.buyBox.singleMemberLlc.bulletPoint3' }
  ],
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_BULLET_POINTS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_BULLET_POINTS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_BULLET_POINTS
});

const getBulletPointsByTaxClassification = ({ taxClassification }) => (
  BULLET_POINTS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || []
);

const DEFAULT_SUBHEADER = 'productPage.taxFiling.page.subheader';

const SUBHEADER_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: 'productPage.taxFiling.page.singleMemberLlc.subheader',
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_SUBHEADER,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_SUBHEADER,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_SUBHEADER
});

const getSubheaderByTaxClassification = ({ taxClassification }) => (
  SUBHEADER_BY_TAX_CLASSIFICATION_MAP[taxClassification] || DEFAULT_SUBHEADER
);

const BENEFITS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      title: 'productPage.taxFiling.valueProp.sCorporation.propTitle1',
      description: 'productPage.taxFiling.valueProp.sCorporation.shortCopy1'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      title: 'productPage.taxFiling.valueProp.sCorporation.propTitle2',
      description: 'productPage.taxFiling.valueProp.sCorporation.shortCopy2'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      title: 'productPage.taxFiling.valueProp.sCorporation.propTitle3',
      description: 'productPage.taxFiling.valueProp.sCorporation.shortCopy3'
    }
  ],
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.taxFiling.valueProp.partnership.propTitle1',
      description: 'productPage.taxFiling.valueProp.partnership.shortCopy1'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.taxFiling.valueProp.partnership.propTitle2',
      description: 'productPage.taxFiling.valueProp.partnership.shortCopy2'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.taxFiling.valueProp.partnership.propTitle3',
      description: 'productPage.taxFiling.valueProp.partnership.shortCopy3'
    }
  ],
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.taxFiling.valueProp.cCorporation.propTitle1',
      description: 'productPage.taxFiling.valueProp.cCorporation.shortCopy1'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.taxFiling.valueProp.cCorporation.propTitle2',
      description: 'productPage.taxFiling.valueProp.cCorporation.shortCopy2'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.taxFiling.valueProp.cCorporation.propTitle3',
      description: 'productPage.taxFiling.valueProp.cCorporation.shortCopy3'
    }
  ]
});

const BENEFIT_ITEMS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    {
      icon: '/s2/material-icons/percentrounded.svg',
      title: 'productPage.taxFiling.valueProp.propTitle1',
      description: 'productPage.taxFiling.valueProp.shortCopy1'
    },
    {
      icon: '/s2/material-icons/insertinvitationrounded.svg',
      title: 'productPage.taxFiling.valueProp.propTitle2',
      description: 'productPage.taxFiling.valueProp.shortCopy2'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      title: 'productPage.taxFiling.valueProp.propTitle3',
      description: 'productPage.taxFiling.valueProp.shortCopy3'
    }
  ],
  // Benefits with tabbed categories
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: BENEFITS_BY_TAX_CLASSIFICATION_MAP,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: BENEFITS_BY_TAX_CLASSIFICATION_MAP,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: BENEFITS_BY_TAX_CLASSIFICATION_MAP
});

const getBenefitByTaxClassification = ({ taxClassification }) => ({
  title: taxClassification === BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC ? 'productPage.taxFiling.valueProp.singleMemberLlc.title' : 'productPage.taxFiling.valueProp.title',
  items: BENEFIT_ITEMS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || [],
  defaultCategory: TAX_CLASSIFICATION_TITLE_MAP[taxClassification]
});

const FOOTNOTES_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    {
      id: '*',
      text: 'productPage.taxFiling.footnotes.1.text',
      link: 'https://higherlogicdownload.s3.amazonaws.com/NSACCT/725010a8-142f-4092-8b5d-077c2618c728/UploadedImages/Membership/IncomeandFeeSurvey/NSA2020-2021_IncomeandFeesStudy_NationalAverages.pdf'
    }
  ]
});

const getFootnotesByTaxClassification = ({ taxClassification }) => ({
  divider: true,
  items: FOOTNOTES_BY_TAX_CLASSIFICATION_MAP[taxClassification] || []
});

const DEFAULT_WHATS_INCLUDED_ITEMS = [
  {
    title: 'productPage.taxFiling.whatsIncluded.item1',
    description: 'productPage.taxFiling.whatsIncluded.item1.descriptionItems'
  },
  {
    title: 'productPage.taxFiling.whatsIncluded.item2',
    description: 'productPage.taxFiling.whatsIncluded.item2.descriptionItems'
  }
];

const WHATS_INCLUDED_ITEMS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    {
      title: 'productPage.taxFiling.whatsIncluded.singleMemberLlc.item1',
      description: 'productPage.taxFiling.whatsIncluded.singleMemberLlc.item1.descriptionItems'
    },
    {
      title: 'productPage.taxFiling.whatsIncluded.singleMemberLlc.item2',
      description: 'productPage.taxFiling.whatsIncluded.singleMemberLlc.item2.descriptionItems'
    }
  ],
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_WHATS_INCLUDED_ITEMS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_WHATS_INCLUDED_ITEMS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_WHATS_INCLUDED_ITEMS
});

const getWhatsIncludedItemsByTaxClassification = ({ taxClassification }) => (
  WHATS_INCLUDED_ITEMS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || []
);

const DEFAULT_BANNER = {
  title: 'productPage.taxFiling.banner.title',
  variant: 'promo',
  chipLabel: 'productPage.taxFiling.banner.chipLabel',
  footer: {
    text: 'productPage.taxFiling.banner.footer',
    link: 'https://quartermastertax.com/small-business-taxes-shocking/#:~:text=According%20to%20a%20study%20by,used%20for%20other%20essential%20purposes'
  }
};

const BANNER_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_BANNER,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_BANNER,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_BANNER
});

const getBannerByTaxClassification = ({ taxClassification }) => (
  BANNER_BY_TAX_CLASSIFICATION_MAP[taxClassification] || null
);

export const TAX_FILING_CONFIG = {
  isTaxClassificationDependent: true,
  hero: {
    headline: 'productPage.taxFiling.page.headline',
    subheader: getSubheaderByTaxClassification,
    headerPicture: {
      src: '/s2/raster/happy-woman-using-calculator.png',
      alt: 'productPage.taxFiling.heroPicture.altText'
    },
    partnerLogo: {
      src: '/s2/vector/april-logo-purple.svg',
      alt: 'productPage.taxFiling.partnerLogo.altText'
    },
    partnerReviewBlanks: [
      {
        text: 'productPage.taxFiling.partnerReviewBlanks.trustpilot.reviewText'
      },
      {
        src: '/illustration/trustpilot-4.5-star.svg',
        showOnMobile: true,
        alt: 'productPage.taxFiling.partnerReviewBlanks.trustpilot.reviewStarAltText'
      },
      {
        src: '/illustration/trustpilot-logo.svg',
        alt: 'productPage.taxFiling.partnerReviewBlanks.trustpilot.reviewLogoAltText'
      }
    ],
    buyBox: [{
      bulletPoints: getBulletPointsByTaxClassification,
      productName: 'productPage.taxFiling.buyBox.productName',
      interactService: {
        ctaType: 'internalCheckout',
        service: ({ taxClassification }) => TAX_CLASSIFICATION_TO_SERVICE_MAP[taxClassification] || 'zb_tax_full_service_biz_per_llc',
        interval: 'annual'
      }
    }]
  },
  banner: getBannerByTaxClassification,
  benefit: getBenefitByTaxClassification,
  howItWorks: {
    title: 'productPage.taxFiling.howItWorks.title',
    items: [
      {
        title: 'productPage.taxFiling.howItWorks.step1',
        description: 'productPage.taxFiling.howItWorks.shortDescription1'
      },
      {
        title: 'productPage.taxFiling.howItWorks.step2',
        description: 'productPage.taxFiling.howItWorks.shortDescription2'
      },
      {
        title: 'productPage.taxFiling.howItWorks.step3',
        description: 'productPage.taxFiling.howItWorks.shortDescription3'
      }
    ],
    image: '/s2/raster/barista-smiling-watch.png',
    alt: 'productPage.taxFiling.howItWorks.altText',
    hasNumber: true,
    hasDivider: false,
    hasMobileImage: true
  },
  whatsIncluded: {
    title: 'productPage.taxFiling.whatsIncluded.title',
    items: getWhatsIncludedItemsByTaxClassification,
    image: '/s2/raster/tax-filing-process.png',
    alt: 'productPage.taxFiling.whatsIncluded.altText',
    hasIcon: true,
    hasDivider: false,
    hasNumber: false,
    hasMobileImage: false
  },
  relatedProducts: {
    title: 'productPage.taxFiling.relatedProducts.title',
    items: [
      {
        title: 'productPage.taxFiling.relatedProducts.quarterlyTaxPlanning.title',
        description: 'productPage.taxFiling.relatedProducts.quarterlyTaxPlanning.description',
        productSlug: 'quarterly-tax-planning',
        entitlement: 'zb_quarterly_tax_planning'
      },
      {
        title: 'dashboard.products.money_pro.title',
        description: 'dashboard.products.money_pro.description',
        productSlug: 'money-pro',
        entitlement: 'money_pro'
      }
    ]
  },
  footnotes: getFootnotesByTaxClassification,
  assistance: {
    title: 'productPage.taxFiling.needAssistance.title',
    getInTouch: 'productPage.taxFiling.needAssistance.getInTouch',
    actions: 'productPage.taxFiling.needAssistance.actions',
    additionalAssistance: [
      {
        icon: '/s2/material-icons/insertinvitationrounded.svg',
        title: 'productPage.taxFiling.needAssistance.expert.title',
        actions: 'productPage.taxFiling.needAssistance.expert.cta',
        href: 'https://calendly.com/d/cvh6-zw4-zkp/tax-consultation'
      }
    ]
  },
  // Configure product gates (feature flags, component with custom logic, etc.) useful for conditional redirects
  productGates: {
    flagGate: {
      flagKey: RELEASE_APRIL_TAX_SERVICES,
      redirectPath: '/products/tax-advice'
    },
    componentGates: [{
      Component: TaxPartnerExclusivityGate,
      props: {
        partnerName: BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS.APRIL,
        redirectPath: '/products/tax-advice'
      }
    }, {
      Component: TaxClassificationGate,
      props: {
        flagKey: RELEASE_APRIL_TAX_SERVICES_PHASE_2,
        shouldRedirect: ({ taxClassification, flagValue, entitlements }) => {
          const hasTaxFilingEntitlement = entitlements.has(
            TAX_CLASSIFICATION_TO_SERVICE_MAP[taxClassification]
          );
          // If the user already has the tax filing entitlement, we don't need to redirect
          if (hasTaxFilingEntitlement) {
            return false;
          }

          if (!flagValue) {
            // For phase 1, we can only sell SM LLC
            return taxClassification !== BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC;
          }
          // For phase 2, we can sell all tax classifications
          return ![
            BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC,
            BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP,
            BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION,
            BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION
          ].includes(taxClassification);
        },
        redirectPath: '/products/tax-advice'
      }
    }]
  },
  getBuyButtonConfig: (entitlements, service) => getBuyButtonConfig({
    entitlements,
    service,
    ctaText: 'productPage.taxFiling.buyBox.buttonLabel'
  })
};
